import { MicrophonePermissionService } from '@app/core/services/microphone-permission.service';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { getWaveBlob } from 'webm-to-wav-converter';

export class VoiceMessageRecorder {
  private readonly stateSubject = new BehaviorSubject<VoiceMessageRecordingState>('idle');
  public readonly state$ = this.stateSubject.asObservable();

  private readonly elapsedTimeSubject = new BehaviorSubject<number>(0);
  public readonly elapsedTime$ = this.elapsedTimeSubject.asObservable();

  public readonly isRecording$ = this.state$.pipe(
    map((state) => state === 'recording'),
    distinctUntilChanged()
  );

  public isRecording = false;

  public analyzerFFTSize = 2048; // set the value before starting recording
  public failReason?: RecordingFailReason;

  public get duration(): number {
    return this._duration;
  }

  public get maxDuration(): number | undefined {
    return this._maxDuration;
  }

  public get analyser(): AnalyserNode | undefined {
    return this._analyser;
  }

  private _maxDuration?: number; // max duration of recording in seconds

  private _duration = 0;
  private recorder?: MediaRecorder;
  private stream: MediaStream | null = null;
  private recorderTimeout?: NodeJS.Timeout;
  private _analyser?: AnalyserNode;
  private sampleRate?: number;
  private deviceId?: string;

  private mediaBlob?: Blob;
  private blobResolver?: (blob: Blob) => void;

  constructor(private microphonePermissionService: MicrophonePermissionService) {
    this.isRecording$.subscribe((recording) => {
      this.isRecording = recording;
    });
  }

  public async startRecording(maxDurationSeconds?: number) {
    return this.start(maxDurationSeconds);
  }

  public stopRecording() {
    this.stop();
  }

  public cancelRecording() {
    if (this.stateSubject.value === 'recording') {
      this.stateSubject.next('canceled');
      this.stop();
    }
  }

  public setSampleRate(sampleRate: number) {
    this.sampleRate = sampleRate;
  }

  public setDeviceId(deviceId: string) {
    this.deviceId = deviceId;
  }

  public async getRecordingAsBlob(): Promise<Blob> {
    if (this.mediaBlob) {
      return this.mediaBlob;
    } else {
      return new Promise<Blob>((resolve) => {
        this.blobResolver = resolve;
      });
    }
  }

  public async getRecordingAsWavFile(name: string): Promise<File> {
    const blob = await this.getRecordingAsBlob();

    return new File([blob], name, { type: 'audio/wav' });
  }

  private async start(maxDurationSeconds?: number) {
    if (
      this.stateSubject.value !== 'recording' &&
      (await this.microphonePermissionService.check()) &&
      (await this.microphonePermissionService.checkMicrophoneAvailability())
    ) {
      this.cleanup();

      try {
        // Recordings always use the browser/OS suppressor. The "Advanced noise suppression"
        // preference selects the DeepFilterNet model for SIP calls only (CUC-3821/CUC-3823);
        // there is no unsuppressed capture state on any surface, so this is a constant rather
        // than a read of the preference. Mirrors SipjsService.getCallMediaConstraints(). CUC-3826.
        const audio: MediaTrackConstraints = { noiseSuppression: true };
        if (this.deviceId) {
          audio.deviceId = this.deviceId;
        }
        this.stream = await navigator.mediaDevices.getUserMedia({ audio });
        const { source, destination, analyser } = this.setupAudioContextAndNodes(this.stream);
        this.setupAndStartRecorder(destination, maxDurationSeconds);
        source.connect(destination);

        this._analyser = analyser;

        this.stateSubject.next('recording');
      } catch (error) {
        console.error('Voice message start recording error:', error);
        this.failReason = RecordingFailReason.InitializationError;
        this.stateSubject.next('failed');
      }
    }
  }

  private stop() {
    this.recorder?.stop();

    if (this.recorderTimeout) {
      clearInterval(this.recorderTimeout);
      this.recorderTimeout = undefined;
    }

    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }
  }

  private cleanup() {
    this._duration = 0;
    this._analyser = undefined;

    this.recorder = undefined;
    this.stream = null;
    this.recorderTimeout = undefined;
    this.failReason = undefined;

    this.mediaBlob = undefined;
    this.blobResolver = undefined;

    this.elapsedTimeSubject.next(0);
  }

  private setupAudioContextAndNodes(audio: MediaStream | HTMLAudioElement) {
    const audioContext = new AudioContext();
    const source =
      audio instanceof HTMLAudioElement
        ? audioContext.createMediaElementSource(audio)
        : audioContext.createMediaStreamSource(audio);
    const destination = audioContext.createMediaStreamDestination();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = this.analyzerFFTSize;
    source.connect(analyser);

    return { source, destination, analyser };
  }

  private setupAndStartRecorder(destination: MediaStreamAudioDestinationNode, maxDuration?: number) {
    this._maxDuration = maxDuration;

    const recordingIntervalId = setInterval(() => {
      this._duration++;
      this.elapsedTimeSubject.next(this._duration);
    }, 1000);

    this.recorder = new MediaRecorder(destination.stream);
    const chunks: Blob[] = [];
    this.recorder.ondataavailable = (event) => {
      chunks.push(event.data);
    };
    this.recorder.onstop = async () => {
      if (this.stateSubject.value !== 'canceled') {
        this.stateSubject.next('finished');

        const audioBlob = new Blob(chunks, { type: 'audio/webm' });

        try {
          this.mediaBlob = await getWaveBlob(
            audioBlob,
            false,
            this.sampleRate ? { sampleRate: this.sampleRate } : undefined
          );

          if (this.blobResolver) {
            this.blobResolver(this.mediaBlob);
          }
        } catch (error) {
          console.error('Error converting blob format:', error);
          this.failReason = RecordingFailReason.DataProcessingError;
          this.stateSubject.next('failed');
        }
      } else {
        this.cleanup();
      }

      clearInterval(recordingIntervalId);
    };

    this.recorder.start();

    if (maxDuration) {
      this.recorderTimeout = setTimeout(() => {
        this.stopRecording();
      }, maxDuration * 1000);
    }
  }
}

export type VoiceMessageRecordingState = 'idle' | 'recording' | 'finished' | 'canceled' | 'failed';

export enum RecordingFailReason {
  InitializationError,
  DataProcessingError,
}
